import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useMarkEstimateAccepted, useMarkEstimateDeclined } from '@/hooks/useEstimates';
import type { Estimate } from '@/types/estimate';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface AcceptDeclineDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimate: Estimate;
}

export function AcceptDeclineDialog({ 
  open, 
  onOpenChange, 
  estimate 
}: AcceptDeclineDialogProps) {
  const markAccepted = useMarkEstimateAccepted();
  const markDeclined = useMarkEstimateDeclined();
  const [action, setAction] = useState<'accepted' | 'declined'>('accepted');
  const [declineReason, setDeclineReason] = useState('');

  const isLoading = markAccepted.isPending || markDeclined.isPending;

  const handleConfirm = () => {
    if (action === 'accepted') {
      markAccepted.mutate(estimate.id, {
        onSuccess: () => {
          onOpenChange(false);
        },
      });
    } else {
      markDeclined.mutate(
        { estimateId: estimate.id, reason: declineReason || undefined },
        {
          onSuccess: () => {
            onOpenChange(false);
            setDeclineReason('');
          },
        }
      );
    }
  };

  const { formatMoney: formatCurrency } = useCompanyLocale();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Update Estimate Status</DialogTitle>
          <DialogDescription>
            Mark estimate {estimate.estimate_number} ({formatCurrency(estimate.estimate_amount)}) as accepted or declined
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup
            value={action}
            onValueChange={(value) => setAction(value as 'accepted' | 'declined')}
          >
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="accepted" id="accepted" />
              <Label htmlFor="accepted" className="flex items-center gap-2 cursor-pointer flex-1">
                <CheckCircle className="h-5 w-5 text-success" />
                <div>
                  <p className="font-medium">Accepted</p>
                  <p className="text-sm text-muted-foreground">Customer has accepted this estimate</p>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-muted/50 cursor-pointer">
              <RadioGroupItem value="declined" id="declined" />
              <Label htmlFor="declined" className="flex items-center gap-2 cursor-pointer flex-1">
                <XCircle className="h-5 w-5 text-destructive" />
                <div>
                  <p className="font-medium">Declined</p>
                  <p className="text-sm text-muted-foreground">Customer has declined this estimate</p>
                </div>
              </Label>
            </div>
          </RadioGroup>

          {action === 'declined' && (
            <div>
              <Label htmlFor="reason">Reason for Decline (Optional)</Label>
              <Textarea
                id="reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="e.g., Too expensive, Went with competitor, No longer needed..."
                rows={3}
                className="mt-2"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading}
            variant={action === 'accepted' ? 'default' : 'destructive'}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {action === 'accepted' ? 'Mark Accepted' : 'Mark Declined'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
